import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth, UserClaims } from '@okta/okta-auth-js';
import { NGXLogger } from 'ngx-logger';
import { EMPTY, Observable, from, map, switchMap } from 'rxjs';
import { APP_CONFIG, AppConfig } from '../../utils/config/app-config';
import { HeaderComponent } from '../header/header.component';
@Component({
    selector: 'acm-header-container',
    imports: [CommonModule, HeaderComponent],
    templateUrl: './header-container.component.html',
    styleUrls: ['./header-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderContainerComponent {
    private logger: NGXLogger = inject(NGXLogger);
    private oktaAuth: OktaAuth = inject(OKTA_AUTH);
    private authStateService: OktaAuthStateService = inject(OktaAuthStateService);
    private appConfig: AppConfig = inject(APP_CONFIG);

    user$: Observable<{ displayName: string; displayInitials: string } | undefined>;
    envDisplayName = 'unknown';
    isProd = false;

    constructor() {
        // user will likely come in as an object
        this.user$ = this.authStateService.authState$.pipe(
            switchMap((authState) => {
                if (authState?.isAuthenticated) {
                    return from(this.oktaAuth.getUser()).pipe(
                        map((claims) => this.mapClaimsToName(claims))
                    );
                } else {
                    return EMPTY;
                }
            })
        );

        this.envDisplayName = this.appConfig.environmentDisplayName;
        this.isProd = this.appConfig.environmentName.includes('prod');
    }

    /**
     * Maps a UserClaims to a name. Sometimes the user claims are structured
     * differently, such as when using end to end testing.
     *
     * called from within authenticated callback.
     */
    private mapClaimsToName(claims: UserClaims): {
        displayName: string;
        displayInitials: string;
    } {
        const displayName =
            claims.name ?? claims.preferred_username ?? claims.email ?? 'Authenticated';
        const displayInitials = displayName
            .split(' ')
            .map((name) => name[0])
            .join('');
        return { displayName, displayInitials };
    }

    async onLogout() {
        // dispatch header logout action
        this.logger.debug('onLogout');
        await this.oktaAuth.signOut();
    }
}
